import convex from '@turf/convex'
import { featureCollection, point, polygon } from '@turf/helpers'
import type { Feature, MultiPolygon, Polygon } from '@turf/helpers'

/** A point with x,y coordinates */
interface Point2D {
  x: number
  y: number
}

/** A cell with position, cluster ID, and filter flag */
interface CellWithCluster {
  x: number
  y: number
  clusterId: number | string | undefined
  isFiltered?: boolean
}

/** Outline path (SVG) and how many cells it contains */
export interface ClusterOutlineResult {
  path: string
  pointCount: number
}

/** Calculate centroid of points */
function calculateCentroid(points: Point2D[]): Point2D {
  const sum = points.reduce(
    (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }),
    { x: 0, y: 0 }
  )
  return {
    x: sum.x / points.length,
    y: sum.y / points.length,
  }
}

/** Calculate distance between two points */
function distance(p1: Point2D, p2: Point2D): number {
  return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2)
}

/** Calculate quartile value from sorted array */
function quartile(sortedValues: number[], q: number): number {
  const pos = (sortedValues.length - 1) * q
  const base = Math.floor(pos)
  const rest = pos - base
  if (sortedValues[base + 1] !== undefined) {
    return sortedValues[base] + rest * (sortedValues[base + 1] - sortedValues[base])
  }
  return sortedValues[base]
}

/** Remove outliers based on distance from centroid using IQR method */
function removeOutliers(points: Point2D[], iqrThreshold = 1.5): Point2D[] {
  if (points.length < 4) return points

  const centroid = calculateCentroid(points)
  const distances = points.map((p) => distance(p, centroid))

  // Calculate IQR
  const sortedDistances = [...distances].sort((a, b) => a - b)
  const q1 = quartile(sortedDistances, 0.25)
  const q3 = quartile(sortedDistances, 0.75)
  const iqr = q3 - q1

  // Remove points beyond Q3 + iqrThreshold * IQR
  const threshold = q3 + iqrThreshold * iqr

  return points.filter((_, i) => distances[i] <= threshold)
}

/**
 * Expand polygon vertices outward from centroid
 * Works in arbitrary coordinate space (e.g., UMAP embeddings)
 * @param coordinates - Polygon ring coordinates
 * @param centroid - Center point to expand from
 * @param scaleFactor - How much to scale (1.25 = 25% expansion)
 * @returns Expanded coordinates
 */
function expandPolygonRing(
  coordinates: number[][],
  centroid: Point2D,
  scaleFactor: number
): number[][] {
  return coordinates.map(([x, y]) => {
    // Vector from centroid to vertex
    const dx = x - centroid.x
    const dy = y - centroid.y

    // Scale the vector and add back to centroid
    return [centroid.x + dx * scaleFactor, centroid.y + dy * scaleFactor]
  })
}

/**
 * Expand hull by a percentage in arbitrary coordinate space
 * @param hull - The polygon hull to expand
 * @param groupPoints - Original points to calculate centroid
 * @param bufferPercent - Percentage to expand (default: 0.10 = 10%)
 * @returns Buffered hull or null if buffering fails
 */
function expandHull(
  hull: Feature<Polygon | MultiPolygon> | null,
  groupPoints: Point2D[],
  bufferPercent = 0.20
): Feature<Polygon | MultiPolygon> | null {
  if (
    !hull ||
    !hull.geometry ||
    (hull.geometry.type !== 'Polygon' && hull.geometry.type !== 'MultiPolygon')
  ) {
    return null
  }

  const centroid = calculateCentroid(groupPoints)
  const scaleFactor = 1 + bufferPercent

  if (hull.geometry.type === 'Polygon') {
    const expandedCoords = hull.geometry.coordinates.map((ring) =>
      expandPolygonRing(ring, centroid, scaleFactor)
    )
    return polygon(expandedCoords)
  }

  // For MultiPolygon (rare case), expand each polygon
  const expandedPolygons = hull.geometry.coordinates.map((polygonCoords) =>
    polygonCoords.map((ring) => expandPolygonRing(ring, centroid, scaleFactor))
  )

  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'MultiPolygon',
      coordinates: expandedPolygons,
    },
  }
}

/** Draw a hull outline around a group of points */
function createHullOutline(
  groupPoints: Point2D[],
): ClusterOutlineResult | null {
  try {
    const turfPoints = groupPoints.map((p) => point([p.x, p.y]))
    const collection = featureCollection(turfPoints)

    // Use just the convex hull (tried concave hull from turf, and it sometimes fails)
    const hull = convex(collection)

    if (!hull || !hull.geometry) {
      return null
    }

    // Expand hull to ensure points are completely inside
    const bufferedHull = expandHull(hull, groupPoints)

    if (
      !bufferedHull ||
      !bufferedHull.geometry ||
      (bufferedHull.geometry.type !== 'Polygon' && bufferedHull.geometry.type !== 'MultiPolygon')
    ) {
      return null
    }

    // Convert to SVG path string
    // For MultiPolygon, use the first polygon
    const coordinates =
      bufferedHull.geometry.type === 'Polygon'
        ? bufferedHull.geometry.coordinates[0]
        : bufferedHull.geometry.coordinates[0][0]

    if (!coordinates || coordinates.length < 3) {
      return null
    }

    const pathParts: string[] = []
    pathParts.push(`M${coordinates[0][0]},${coordinates[0][1]}`)

    coordinates.slice(1).forEach((coord: number[]) => {
      pathParts.push(`L${coord[0]},${coord[1]}`)
    })

    pathParts.push('Z')

    return {
      path: pathParts.join(' '),
      pointCount: groupPoints.length,
    }
  } catch (error) {
    return null
  }
}

/**
 * Create a hull outline for a Leiden cluster
 * - Filters out hidden cells
 * - Creates a single smooth boundary around all points
 * - Uses concave hull for tight fit, convex hull as fallback
 * - Returns all cells within the boundary
 */
export const calculateClusterOutline = (
  allCells: CellWithCluster[],
  targetClusterId: number | string | undefined
): ClusterOutlineResult[] => {
  try {
    // Get only target cluster cells (not filtered)
    // Convert both to strings for comparison to handle number/string type mismatches
    const targetClusterStr = String(targetClusterId)
    const clusterCells = allCells.filter((cell) => {
      const cellClusterStr = String(cell.clusterId)
      const isTarget = cellClusterStr === targetClusterStr
      const isNotFiltered = !cell.isFiltered
      return isTarget && isNotFiltered
    })

    // Need at least 3 points for a polygon
    if (clusterCells.length < 3) {
      return []
    }

    // Remove invalid coordinates
    const validPoints: Point2D[] = clusterCells
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
      .map((p) => ({ x: p.x, y: p.y }))

    if (validPoints.length < 3) {
      return []
    }

    // Remove outliers to prevent extreme points from distorting the hull
    const filteredPoints = removeOutliers(validPoints)

    if (filteredPoints.length < 3) {
      return []
    }

    // Create a single hull outline around filtered points
    const outline = createHullOutline(filteredPoints)

    return outline ? [outline] : []
  } catch (error) {
    console.warn('Failed to calculate cluster outline:', error)
    return []
  }
}

export default calculateClusterOutline
